import { Capacitor } from '@capacitor/core';
import { FileOpener } from '@capawesome-team/capacitor-file-opener';
import * as Sentry from '@sentry/react';
import { t } from 'i18next';
import Swal from 'sweetalert2';
import { mediaFile, reportBrokenMedia } from './mediaCache';

// iOS rejects a second open while the first preview is still on its way.
const ALREADY_OPEN = 'FILE_ALREADY_OPEN';
const NOT_FOUND = 'FILE_NOT_FOUND';

const notify = (key: string, icon: 'warning' | 'error') => {
    void Swal.fire({
        toast: true,
        position: 'top-end',
        icon,
        title: t(key),
        showConfirmButton: false,
        timer: 6000,
        timerProgressBar: true,
    });
};

const show = async (uri: string, mime: string | undefined): Promise<void> => {
    try {
        await FileOpener.openFile({ path: uri, mimeType: mime });
    } catch (error) {
        const code = (error as { code?: string } | null)?.code;
        if (code === ALREADY_OPEN) return;
        // The file left the phone outside the app. The next tap downloads it again.
        if (code === NOT_FOUND) reportBrokenMedia(Capacitor.convertFileSrc(uri));
        console.error('file opener failed', code, error);
        Sentry.captureException(error, { tags: { 'media.open': code ?? 'unknown' } });
        notify('Could not open the file', 'error');
    }
};

/**
 * Opens a stored file in the phone's viewer, from the device media cache. On a miss the file
 * downloads first. Offline with no cached file, and when the download or the viewer fails, the
 * user sees a toast.
 *
 * Where the cache does not keep the URL (the web, the cache off), it calls `fallback` before it
 * returns, so a browser still counts a new tab as the user's own action.
 */
export const openStoredFile = (url: string, mime: string | undefined, fallback: () => void): void => {
    const file = mediaFile(url);
    if (file === null) {
        fallback();
        return;
    }
    void file.then((result) => {
        if (result.kind === 'file') return show(result.uri, mime);
        if (result.kind === 'offline') notify('Unavailable while offline', 'warning');
        else notify('Could not open the file', 'error');
    });
};
